import { GrElement, TOKENS_CSS } from "../core/base";
import { createAdapter, transportDefinition, type Adapter } from "../adapters/adapter";
import type { ConnectionState, DeviceDescriptor } from "../core/messages";
import type { ParseMode } from "../core/codec";
import {
  clearOverride,
  connectionOptionsFromFields,
  connectionTransports,
  effectiveConnection,
  loadOverride,
  pageOverrideKey,
  requiresGesture,
  saveOverride,
  type ConnectionOverride,
} from "../core/connection-prefs";
import { safeStorage } from "../core/safe-storage";
import { transportSupported } from "../core/capabilities";

/**
 * <gr-connect device="co2" transport="serial" baud="115200"></gr-connect>
 * <gr-connect device="plant" transport="mqtt" url="wss://..." topic="root/#" auto></gr-connect>
 *
 * Owns one transport connection, renders the connect button + status pill,
 * publishes `<device>/$state` and `<device>/$descriptor` on the bus.
 *
 * `auto` connects on load without a click — for unattended pages
 * (installations, kiosks, gallery cards). Only honored when the effective
 * transport is gesture-free (websocket/mqtt); serial always needs a click.
 *
 * The pill shows the active protocol as a small badge; clicking the badge
 * opens a switcher so the user can change transport (and its settings) at
 * runtime. The choice persists in localStorage per page + device.
 * `switcher="off"` hides the badge for hosts that offer their own source
 * picker (`<gr-input>`) and drive `reloadConnection()`.
 *
 * DESCRIBE handshake: if the firmware answers with a device id that
 * doesn't match this element's `device`, the pill flags the mismatch —
 * the page was generated for different firmware.
 */
export class GrConnect extends GrElement {
  private adapter: Adapter | null = null;
  private state: ConnectionState = "idle";
  private detail = "";
  private descriptor: DeviceDescriptor | null | undefined;
  private override: ConnectionOverride | null = null;
  private panelOpen = false;

  connectedCallback(): void {
    this.override = loadOverride(safeStorage(), this.storageKey());

    const root = this.attachShadow({ mode: "open" });
    root.innerHTML = `
      <style>
        ${TOKENS_CSS}
        :host { display: inline-flex; position: relative; }
        .pill {
          display: inline-flex;
          align-items: center;
          border: 1px solid var(--_border);
          background: var(--_bg);
          border-radius: 999px;
          transition: 0.15s;
        }
        .pill:hover { border-color: var(--_accent); }
        button {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          border: none;
          background: transparent;
          color: var(--_text);
          font-family: var(--_font);
          font-size: 11px;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          padding: 9px 4px 9px 16px;
          cursor: pointer;
        }
        .dot {
          width: 8px;
          height: 8px;
          border-radius: 999px;
          background: var(--_muted);
          transition: 0.2s;
        }
        :host([data-state="connected"]) .dot { background: #34d399; box-shadow: 0 0 8px rgba(52, 211, 153, 0.8); }
        :host([data-state="connecting"]) .dot { background: #fbbf24; }
        :host([data-state="error"]) .dot,
        :host([data-state="mismatch"]) .dot { background: #fb7185; }
        .detail { color: var(--_muted); font-size: 10px; letter-spacing: normal; text-transform: none; }
        .proto {
          border: none;
          background: transparent;
          color: var(--_muted);
          font-family: var(--_mono);
          font-size: 9px;
          letter-spacing: 0.08em;
          text-transform: lowercase;
          padding: 9px 14px 9px 8px;
          cursor: pointer;
        }
        .proto:hover { color: var(--_accent); }
        :host([switcher="off"]) .proto { display: none; }
        :host([switcher="off"]) button.main { padding-right: 16px; }
        .proto::before { content: "·"; margin-right: 8px; color: var(--_border); }
        .proto[data-override]::after { content: "*"; color: var(--_accent); }
        .panel {
          display: none;
          position: absolute;
          top: calc(100% + 8px);
          right: 0;
          z-index: 50;
          min-width: 230px;
          padding: 14px;
          border: 1px solid var(--_border);
          border-radius: 14px;
          background: rgba(12, 12, 16, 0.97);
          box-shadow: 0 16px 48px rgba(0, 0, 0, 0.5);
          flex-direction: column;
          gap: 10px;
        }
        .panel[data-open] { display: flex; }
        .panel .label { font-size: 9px; }
        select, input {
          width: 100%;
          border: 1px solid var(--_border);
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.05);
          color: var(--_text);
          font-family: var(--_mono);
          font-size: 11px;
          padding: 7px 9px;
          outline: none;
        }
        select:focus, input:focus { border-color: var(--_accent); }
        .field { display: flex; flex-direction: column; gap: 4px; }
        .actions { display: flex; gap: 8px; margin-top: 2px; }
        .actions button {
          flex: 1;
          justify-content: center;
          border: 1px solid var(--_border);
          border-radius: 8px;
          padding: 7px 0;
          font-size: 9px;
          letter-spacing: 0.15em;
        }
        .actions .apply { border-color: var(--_accent); color: var(--_accent); }
        .actions button:hover { background: rgba(255, 255, 255, 0.06); }
      </style>
      <span class="pill">
        <button type="button" class="main" part="button">
          <span class="dot" part="dot"></span>
          <span class="text"></span>
          <span class="detail"></span>
        </button>
        <button type="button" class="proto" title="Change protocol"></button>
      </span>
      <div class="panel" part="panel">
        <div class="field">
          <span class="label">Protocol</span>
          <select class="transport"></select>
        </div>
        <div class="fields"></div>
        <div class="actions">
          <button type="button" class="apply">Apply</button>
          <button type="button" class="reset">Page default</button>
        </div>
      </div>
    `;

    root.querySelector(".main")?.addEventListener("click", () => void this.toggle());
    root.querySelector(".proto")?.addEventListener("click", (e) => {
      e.stopPropagation();
      this.panelOpen = !this.panelOpen;
      if (this.panelOpen) this.populatePanel();
      this.renderPanel();
    });
    root.querySelector(".apply")?.addEventListener("click", () => void this.applyPanel());
    root.querySelector(".reset")?.addEventListener("click", () => void this.resetOverride());
    root.querySelector<HTMLSelectElement>(".transport")?.addEventListener("change", () => {
      this.populateFields(root.querySelector<HTMLSelectElement>(".transport")!.value);
    });
    document.addEventListener("click", this.onDocumentClick);

    if (!this.checkTransport()) return;
    this.render();

    if (this.hasAttribute("auto") && !requiresGesture(this.effective().transport)) {
      void this.connectNow();
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener("click", this.onDocumentClick);
    void this.adapter?.disconnect();
    this.adapter = null;
  }

  private onDocumentClick = (e: MouseEvent): void => {
    if (this.panelOpen && !e.composedPath().includes(this)) {
      this.panelOpen = false;
      this.renderPanel();
    }
  };

  private get device(): string {
    return this.attr("device", "device");
  }

  private storageKey(): string {
    return pageOverrideKey(this.device);
  }

  /**
   * Re-read the stored override and drop the live connection. For hosts that
   * write the override themselves — the picker in `<gr-input>` — so the pill
   * agrees with the block around it. Reconnecting stays a user gesture.
   */
  async reloadConnection(): Promise<void> {
    await this.setConnectionOverride(loadOverride(safeStorage(), this.storageKey()));
  }

  /** Apply settings without putting session-only MQTT credentials in storage. */
  async setConnectionOverride(override: ConnectionOverride | null): Promise<void> {
    this.override = override;
    if (override) saveOverride(safeStorage(), this.storageKey(), override);
    else clearOverride(safeStorage(), this.storageKey());
    await this.adapter?.disconnect();
    this.adapter = null;
    this.descriptor = undefined;
    if (this.checkTransport()) this.onState("idle");
  }

  /** Effective transport + options, for a host rendering its own picker. */
  connectionSettings(): { transport: string; options: Record<string, string> } {
    return this.effective();
  }

  private attrsRecord(): Record<string, string> {
    const attrs: Record<string, string> = {};
    for (const attribute of Array.from(this.attributes)) {
      attrs[attribute.name.toLowerCase()] = attribute.value;
    }
    return attrs;
  }

  private effective(): { transport: string; options: Record<string, string> } {
    return effectiveConnection(this.attrsRecord(), this.override);
  }

  private checkTransport(): boolean {
    const { transport } = this.effective();
    if (transportDefinition(transport)) return true;
    this.onState("error", `Unknown transport "${transport}". Choose an available protocol.`);
    return false;
  }

  private async toggle(): Promise<void> {
    if (this.state === "connected" || this.state === "connecting" || this.state === "mismatch") {
      await this.adapter?.disconnect();
      this.adapter = null;
      return;
    }
    await this.connectNow();
  }

  private async connectNow(): Promise<void> {
    // Kill any previous adapter first — a websocket adapter keeps retrying
    // on its own after reporting "disconnected", and connecting on top of
    // it would double-attach to the bus.
    await this.adapter?.disconnect();
    this.adapter = null;
    if (!this.checkTransport()) return;
    const { transport, options } = this.effective();
    try {
      this.adapter = createAdapter(transport, {
        device: this.device,
        bus: this.bus,
        parse: (options.parse ?? "auto") as ParseMode,
        options,
        onState: (state, detail) => this.onState(state, detail),
        onDescriptor: (descriptor) => this.onDescriptor(descriptor),
      });
      await this.adapter.connect();
    } catch (error) {
      this.onState("error", error instanceof Error ? error.message : String(error));
    }
  }

  // ── Protocol switcher panel ────────────────────────────────────────────

  private populatePanel(): void {
    const root = this.shadowRoot;
    if (!root) return;
    const select = root.querySelector<HTMLSelectElement>(".transport")!;
    const { transport } = this.effective();
    const offered: Array<{ name: string; label?: string }> = connectionTransports();
    if (!transportDefinition(transport)) offered.unshift({ name: transport });
    select.replaceChildren(...offered.map((definition) => {
      const supported = transportSupported(definition.name);
      const label = definition.label ?? definition.name;
      const option = document.createElement("option");
      option.value = definition.name;
      option.selected = definition.name === transport;
      option.disabled = !supported;
      option.textContent = supported ? label : `${label} (unavailable)`;
      return option;
    }));
    this.populateFields(transport);
  }

  private populateFields(transport: string): void {
    const root = this.shadowRoot;
    if (!root) return;
    const container = root.querySelector(".fields")!;
    const { options } = this.effective();
    container.innerHTML = "";
    for (const field of transportDefinition(transport)?.fields ?? []) {
      const wrap = document.createElement("div");
      wrap.className = "field";
      const label = document.createElement("span");
      label.className = "label";
      label.textContent = field.label;
      const input = document.createElement("input");
      input.name = field.name;
      input.type = field.type ?? "text";
      input.autocomplete = "off";
      input.spellcheck = false;
      input.setAttribute("aria-label", field.label);
      input.placeholder = field.placeholder;
      input.value = options[field.name] ?? "";
      wrap.append(label, input);
      container.appendChild(wrap);
    }
  }

  private async applyPanel(): Promise<void> {
    const root = this.shadowRoot;
    if (!root) return;
    const transport = root.querySelector<HTMLSelectElement>(".transport")!.value;
    const options = connectionOptionsFromFields(
      transport,
      root.querySelectorAll<HTMLInputElement>(".fields input"),
    );
    this.panelOpen = false;
    await this.setConnectionOverride({ transport, options });
    // The Apply click is a user gesture, so serial requestPort() is allowed.
    await this.connectNow();
  }

  private async resetOverride(): Promise<void> {
    this.panelOpen = false;
    await this.setConnectionOverride(null);
  }

  private renderPanel(): void {
    const panel = this.shadowRoot?.querySelector(".panel");
    panel?.toggleAttribute("data-open", this.panelOpen);
  }

  // ── State + rendering ──────────────────────────────────────────────────

  private onState(state: ConnectionState, detail?: string): void {
    // Keep a handshake mismatch visible instead of overwriting it.
    if (this.state === "mismatch" && state === "connected") return;
    this.state = state;
    this.detail = detail ?? "";
    this.bus.publish(`${this.device}/$state`, state, { src: "gr-connect" });
    this.render();
  }

  private onDescriptor(descriptor: DeviceDescriptor | null): void {
    if (this.descriptor !== undefined) return;
    this.descriptor = descriptor;
    this.bus.publish(
      `${this.device}/$descriptor`,
      descriptor ? (JSON.parse(JSON.stringify(descriptor)) as Record<string, never>) : null,
      { src: "gr-connect" },
    );
    if (descriptor && descriptor.device !== this.device) {
      this.state = "mismatch";
      this.detail = `firmware says "${descriptor.device}"`;
      this.bus.publish(`${this.device}/$state`, "mismatch", { src: "gr-connect" });
    }
    this.render();
  }

  private render(): void {
    if (!this.shadowRoot) return;
    this.setAttribute("data-state", this.state);
    const text = this.shadowRoot.querySelector(".text");
    const detail = this.shadowRoot.querySelector(".detail");
    const proto = this.shadowRoot.querySelector(".proto");
    if (!text || !detail || !proto) return;
    const labels: Record<ConnectionState, string> = {
      idle: this.attr("label", `Connect ${this.device}`),
      connecting: "Connecting…",
      connected: this.descriptor?.name ?? `${this.device} live`,
      disconnected: this.attr("label", `Connect ${this.device}`),
      mismatch: "Firmware mismatch",
      error: "Error",
    };
    text.textContent = labels[this.state];
    detail.textContent = this.detail;
    proto.textContent = this.effective().transport;
    proto.toggleAttribute("data-override", this.override !== null);
    this.renderPanel();
    // Reset for the next DESCRIBE handshake, but keep the adapter handle:
    // websocket auto-reconnects after reporting "disconnected", and dropping
    // the handle would make that revived connection impossible to close.
    if (this.state === "disconnected") {
      this.descriptor = undefined;
    }
  }
}

customElements.define("gr-connect", GrConnect);
